import { User } from "lucide-react";

interface SessionUserBannerProps {
  displayName: string;
  isLoggedIn: boolean;
  showNotMe?: boolean;
  onNotMe?: () => void;
  variant?: "page" | "modal";
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function SwitchAccountButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 rounded-full border border-emerald-300/90 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm transition-colors hover:border-emerald-400 hover:bg-emerald-50"
    >
      Not you?
    </button>
  );
}

/**
 * Shows who the browser session belongs to, with an optional "Not you?" action
 * when a persisted login is detected.
 */
export function SessionUserBanner({
  displayName,
  isLoggedIn,
  showNotMe = false,
  onNotMe,
  variant = "page",
}: SessionUserBannerProps) {
  const avatar = isLoggedIn ? (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-bold text-white">
      {getInitials(displayName)}
    </span>
  ) : (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-emerald-700">
      <User className="h-4 w-4" />
    </span>
  );

  if (variant === "modal" && isLoggedIn) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200/90 bg-gradient-to-r from-emerald-50 to-emerald-50/40 p-4">
        {avatar}
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-700">
            You&apos;re signed in as{" "}
            <span className="font-semibold text-gray-900">{displayName}</span>
          </p>
          <p className="mt-0.5 text-xs text-gray-500">
            Your seat will be reserved under this account.
          </p>
        </div>
        {showNotMe && onNotMe && <SwitchAccountButton onClick={onNotMe} />}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-emerald-200/70 bg-gradient-to-r from-emerald-50/90 to-emerald-50/30 px-4 py-3">
      {avatar}
      <p className="min-w-0 flex-1 text-sm text-foreground">
        {isLoggedIn ? (
          <>
            Welcome back,{" "}
            <span className="font-semibold">{displayName}</span>
          </>
        ) : (
          <>
            Welcome, <span className="font-semibold">Guest</span>
          </>
        )}
      </p>
      {showNotMe && onNotMe && <SwitchAccountButton onClick={onNotMe} />}
    </div>
  );
}
